/**
 * Dashboard Page
 *
 * Main analytics dashboard with KPIs, charts, and filters
 */

import { PageHeader } from '@/shared/components/PageHeader';
import { exportElementToPDF, exportElementToPNG } from '@/shared/lib/export-image';
import { exportDateStamp } from '@/shared/lib/export-utils';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Calendar, Download, LayoutDashboard, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import DashboardErrorState from '../components/DashboardErrorState';
import DashboardFilters from '../components/DashboardFilters';
import { ChartLoadingSkeleton, KpiLoadingSkeleton } from '../components/DashboardLoadingState';
import JobsChartsSection from '../components/JobsChartsSection';
import KpiSection from '../components/KpiSection';
import PerformanceChartsSection from '../components/PerformanceChartsSection';
import { CHART_COLORS, DATE_RANGE_LABELS, DEFAULT_FILTERS } from '../constants/chartConfig';
import { useDashboardAnalytics } from '../hooks/useDashboardAnalytics';
import type { DashboardFilters as FiltersType } from '../types/analytics';
import {
  clearFiltersFromStorage,
  loadFiltersFromStorage,
  saveFiltersToStorage,
} from '../utils/filterPersistence';
import {
  areFiltersDefault,
  filtersToSearchParams,
  searchParamsToFilters,
} from '../utils/filterUrlSync';

const DASHBOARD_CAPTURE_ID = 'dashboard-export-container';

export default function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [panelOpen, setPanelOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Initialize filters from URL params first, then localStorage, then defaults
  const [filters, setFilters] = useState<FiltersType>(() => {
    // Priority: URL params > localStorage > defaults
    const urlFilters = searchParamsToFilters(searchParams);
    if (urlFilters) {
      return urlFilters;
    }

    const saved = loadFiltersFromStorage();
    return saved || DEFAULT_FILTERS;
  });
  const [draftFilters, setDraftFilters] = useState(filters);

  // Fetch all dashboard analytics data
  const { kpis, jobs, performance } = useDashboardAnalytics(filters);

  // Sync URL with filters on mount if URL is empty but filters are not default
  // This handles the case when navigating back to dashboard with saved filters
  useEffect(() => {
    const urlParams = searchParamsToFilters(searchParams);

    // If URL has no filters but we loaded filters from localStorage (non-default)
    if (!urlParams && !areFiltersDefault(filters)) {
      setSearchParams(filtersToSearchParams(filters), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync draftFilters with filters when filters change from external source
  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  // Derive status distribution for pie chart
  const statusDist = useMemo(() => {
    if (!kpis.data) return [];
    const totals = kpis.data.totals;
    return [
      { name: 'Not Started', value: totals.pending, color: CHART_COLORS.slate, id: 'pending' },
      { name: 'In Progress', value: totals.inProgress, color: CHART_COLORS.blue, id: 'inProgress' },
      { name: 'Completed', value: totals.completed, color: CHART_COLORS.green, id: 'completed' },
      { name: 'Urgent', value: totals.urgent, color: CHART_COLORS.red, id: 'urgent' },
      { name: 'Aborted', value: totals.aborted, color: CHART_COLORS.amber, id: 'aborted' },
    ];
  }, [kpis.data]);

  const applyFilters = () => {
    setFilters(draftFilters);
    saveFiltersToStorage(draftFilters);

    // Update URL params (clear if filters are at defaults)
    if (areFiltersDefault(draftFilters)) {
      setSearchParams(new URLSearchParams(), { replace: true });
    } else {
      setSearchParams(filtersToSearchParams(draftFilters), { replace: true });
    }

    setPanelOpen(false);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setDraftFilters(DEFAULT_FILTERS);
    clearFiltersFromStorage();

    // Clear URL params
    setSearchParams(new URLSearchParams(), { replace: true });

    setPanelOpen(false);
  };

  const activeCount = Object.entries(filters).filter(
    ([, v]) => v !== 'all' && v !== 'last6m',
  ).length;

  // Capture the whole dashboard body (KPIs + charts) to an image/PDF. Reuses
  // the shared element exporter (html2canvas → jsPDF), the same one the Gantt
  // schedule uses.
  const handleExport = async (format: 'pdf' | 'png') => {
    setIsExporting(true);
    try {
      const filename = `dashboard_${exportDateStamp()}.${format}`;
      if (format === 'pdf') {
        await exportElementToPDF(DASHBOARD_CAPTURE_ID, filename);
      } else {
        await exportElementToPNG(DASHBOARD_CAPTURE_ID, filename);
      }
    } catch (error) {
      console.error('Error exporting dashboard:', error);
      showErrorToast('Export failed', 'Could not export the dashboard. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Dashboard"
        subtitle="Operational reporting and analytics overview"
        icon={
          <LayoutDashboard className="w-8 h-8" style={{ color: '#E84A2B' }} strokeWidth={1.5} />
        }
        actions={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-border hover:bg-accent rounded-lg gap-2"
                  disabled={isExporting}
                >
                  <Download className="w-4 h-4" />
                  {isExporting ? 'Exporting…' : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('png')}>
                  Export as Image
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              onClick={() => {
                setDraftFilters(filters);
                setPanelOpen(true);
              }}
              className="border-border hover:bg-accent rounded-lg"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {activeCount > 0 && (
                <Badge className="ml-2 h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-full bg-orange-600 text-white text-[10px]">
                  {activeCount}
                </Badge>
              )}
            </Button>
          </div>
        }
      />

      {/* Scrollable body */}
      <div id={DASHBOARD_CAPTURE_ID} className="flex-1 overflow-y-auto space-y-8 pb-8">
        {/* Active period label */}
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {DATE_RANGE_LABELS[filters.dateRange]}
          </span>
          {activeCount > 0 && (
            <button onClick={clearFilters} className="text-xs text-orange-600 hover:underline ml-1">
              Clear filters
            </button>
          )}
        </div>

        {/* KPI Section */}
        {kpis.isLoading ? (
          <KpiLoadingSkeleton />
        ) : kpis.isError ? (
          <DashboardErrorState
            title="KPI Metrics Unavailable"
            message="We could not load KPI metrics right now."
            onRetry={() => kpis.refetch()}
          />
        ) : kpis.data ? (
          <KpiSection data={kpis.data} />
        ) : null}

        {/* Jobs Analytics Section */}
        {jobs.isLoading ? (
          <>
            <ChartLoadingSkeleton />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartLoadingSkeleton />
              <ChartLoadingSkeleton />
            </div>
            <ChartLoadingSkeleton />
          </>
        ) : jobs.isError ? (
          <DashboardErrorState
            title="Job Analytics Unavailable"
            message="Job trend data is temporarily unavailable."
            onRetry={() => jobs.refetch()}
            lastUpdated={jobs.data?.updatedAt}
          />
        ) : jobs.data ? (
          <JobsChartsSection data={jobs.data} statusDistribution={statusDist} />
        ) : null}

        {/* Performance Analytics Section */}
        {performance.isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartLoadingSkeleton />
            <ChartLoadingSkeleton />
            <ChartLoadingSkeleton />
            <ChartLoadingSkeleton />
          </div>
        ) : performance.isError ? (
          <DashboardErrorState
            title="Performance Analytics Unavailable"
            message="Performance and team data is temporarily unavailable."
            onRetry={() => performance.refetch()}
            lastUpdated={performance.data?.updatedAt}
          />
        ) : performance.data ? (
          <PerformanceChartsSection data={performance.data} />
        ) : null}
      </div>

      {/* Dashboard Filters Component */}
      <DashboardFilters
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        filters={draftFilters}
        onFiltersChange={setDraftFilters}
        onApply={applyFilters}
        onClear={clearFilters}
      />
    </div>
  );
}
